import React, {useMemo} from 'react';

import {useGate, useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {generatePath, useLocation} from 'react-router-dom';

import {ReactComponent as FilterIcon} from 'assets/svg/filter2.svg';
import {ModalFirsError} from 'components/common/ModalFirsError';
import {Page} from 'components/common/Page';
import {DateRange} from 'components/ui-kit/DateRange';
import {Helper} from 'components/ui-kit/Helper';
import {Icon} from 'components/ui-kit/Icon';
import {Link} from 'components/ui-kit/Link';
import {StatusWrapper} from 'components/ui-kit/PageComponents';
import {Select} from 'components/ui-kit/Select';
import {GDS_STATUS_COLORS} from 'core/constants/statuses';
import {GdsStatus} from 'core/dictionaries';
import {ROUTE} from 'core/routes';
import {isCustomsRole$} from 'models/auth';
import {listState$, showConfigBlockChanged} from 'models/documents/documents/grid/list';
import {Gate} from 'models/documents/gds';
import {breakpoint, colors} from 'theme';
import {GdsAccessors, GdsGridFormattedRow, GdsGridRawRow} from 'types';
import {useMatchMedia} from 'utils/custom-hooks';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';
import {mapEnumToI18nOptions} from 'utils/select';

import {Controls} from '../../DocumentsGrid/Controls';
import {GridsNavigation} from '../../GridsNavigation';

import {GDTable} from '../GDTable';

import {TableSearch} from '../GDTable/search';

import {ControlsWrapper, HeaderControls, NavigationWrapper} from './styled';

export function convertData(data: GdsGridRawRow[] | undefined, t: Function): GdsGridFormattedRow[] {
    if (!data) {
        return [];
    }

    const getColors = (status: GdsStatus) => ({
        color: GDS_STATUS_COLORS[status ?? 'DEFAULT'],
    });

    return data.map((elem) => ({
        [GdsAccessors.id]: handleNil(elem.id, () => (
            <Link to={generatePath(ROUTE.DOCUMENTS_GDS_DETAIL, {id: elem.id})}>{elem.id}</Link>
        )),
        [GdsAccessors.customsGdNumber]: handleNil(elem.customsGdNumber),
        [GdsAccessors.customsCode]: handleNil(elem.customsCode),
        [GdsAccessors.dateOfReceipt]: handleNil(elem.receiptedAt, (value: string) =>
            formatDate(new Date(value), 'dd.MM.yyyy HH:mm'),
        ),
        [GdsAccessors.status]: handleNil(elem.status, (value: GdsStatus) => (
            <>
                <StatusWrapper {...getColors(value)}>
                    {t('pages.documents.gds.status.' + value)}
                </StatusWrapper>
                {value === GdsStatus.REJECTED_AUTOMATIC && (
                    <Helper
                        content={t('pages.documents.gds.statusRejectedAutomaticHelper', {
                            defaultValue: 'Rejected automatically due to code disaggregation',
                        })}
                    />
                )}
            </>
        )),
    }));
}

export const GDPage = () => {
    useGate(Gate);
    const location = useLocation();
    const {t} = useTranslation();
    const isMobile = useMatchMedia(`(max-width: ${breakpoint.phone})`);
    const {showConfigBlock} = useStore(listState$);
    const isCustomsRole = useStore(isCustomsRole$);

    const setTableHeaderConfig = useMemo(
        () => [
            {
                id: GdsAccessors.id,
                header: t('pages.documents.gds.columns.id'),
                isSortable: true,
                width: 237,
            },
            {
                id: GdsAccessors.customsGdNumber,
                header: t('pages.documents.gds.columns.customsGdsNumber'),
                isSortable: true,
                width: 260,
            },
            {
                id: GdsAccessors.customsCode,
                header: t('pages.documents.gds.columns.customsCode'),
                isSortable: true,
                width: 212,
            },
            {
                id: GdsAccessors.dateOfReceipt,
                header: t('pages.documents.gds.columns.dateOfReceipt'),
                filter: <DateRange />,
                isSortable: true,
            },
            {
                id: GdsAccessors.status,
                header: t('pages.documents.gds.columns.status'),
                filter: (
                    <Select
                        options={mapEnumToI18nOptions(GdsStatus, 'pages.documents.gds.status')}
                        isClearable
                    />
                ),
                isSortable: false,
                width: 239,
            },
        ],
        [location.pathname, t],
    );

    const headerControls = (
        <HeaderControls>
            <ControlsWrapper>
                <TableSearch />
                {!isCustomsRole && <Controls />}
            </ControlsWrapper>
            <NavigationWrapper needMargin>
                <GridsNavigation />
            </NavigationWrapper>
        </HeaderControls>
    );

    const pageHeaderControls = isMobile ? (
        <Icon
            source={FilterIcon}
            width={18}
            height={18}
            onClick={() => showConfigBlockChanged(!showConfigBlock)}
            fillColor={showConfigBlock ? colors.brandPrimary : colors.black}
        />
    ) : null;

    return (
        <Page title={t('menu.Documents')} controls={pageHeaderControls}>
            <ModalFirsError />
            {!isMobile && (
                <GDTable
                    configFunc={setTableHeaderConfig}
                    dataMapper={convertData}
                    headerControls={headerControls}
                    // exportConfig={exportConfig}
                    headerControlsAlignItems="flex-end"
                />
            )}
            {isMobile && (
                <GDTable
                    configFunc={setTableHeaderConfig}
                    dataMapper={convertData}
                    headerControls={headerControls}
                    // exportConfig={exportConfig}
                />
            )}
        </Page>
    );
};
